import * as d3 from 'd3';
import { Gui } from '@/gui/gui';
import { DetailComponent } from '../detail-component';
import { AccountDataType } from '@/data-management/data-sources/sources/accounts-data-source';
import { AccountNodeData, AccountLinkData, AccountRelationshipType } from './account-detail-contracts';
import { GeneralConstants } from '@/util/app-constants';
import { EmployeeDataType } from '@/data-management/data-sources/sources/employees-data-source';

export class AccountDetailComponent extends DetailComponent<AccountDataType, AccountNodeData, AccountLinkData> {
  private accounts: { [accountId: string]: AccountDataType };

  constructor(gui: Gui, jQueryElement: JQuery<HTMLElement>) {
    super(gui, jQueryElement);
    this.accounts = this.gui.app.dataSourceLoader.accountDataSource.accountIdsToAccounts;
  }

  public setElement(elementId: string) {
    this.selectedElement = this.accounts[elementId];
    this.setSelectedInfo();
  }

  protected initLinks() {
    if (this.selectedElement.AccountPlan) {
      const accountPlanLink = this.jQueryElement.find('.context-detail-component__link--account-plan');
      accountPlanLink.show();
      accountPlanLink.click(() => {
        this.openUrlInNewTab(this.selectedElement.AccountPlan);
      });
    }

    const capacityPlanLink = this.jQueryElement.find('.context-detail-component__link--capacity-plan');
    capacityPlanLink.show();
    capacityPlanLink.click(() => {
      this.openUrlInNewTab(`${GeneralConstants.capacityPlanSearchUrl}${this.selectedElement.Name}`);
    });
  }

  protected cleanLinks() {
    const accountPlanLink = this.jQueryElement.find('.context-detail-component__link--account-plan');
    accountPlanLink.hide();
    accountPlanLink.off('click');
    const capacityPlanLink = this.jQueryElement.find('.context-detail-component__link--capacity-plan');
    capacityPlanLink.hide();
    capacityPlanLink.off('click');
  }

  protected buildSimulationData() {
    this.nodes = [];
    this.links = [];

    const accountCenterNode = new AccountNodeData(this.selectedElement, AccountRelationshipType.None);
    this.nodes.push(accountCenterNode);

    this.addRelation(this.selectedElement.AccountManagerId, AccountRelationshipType.AM);
    this.addRelation(this.selectedElement.AccountDeliveryManagerId, AccountRelationshipType.ADM);
    this.addRelation(this.selectedElement.HRProxyId, AccountRelationshipType.HRProxy);
    this.addRelation(this.selectedElement.AccountSPOCId, AccountRelationshipType.AccountSPOC);
    this.addRelation(this.selectedElement.AccountClusterLeadId, AccountRelationshipType.ACL);
  }

  private addRelation(id: string, relationshipType: AccountRelationshipType) {
    if (id) {
      const employee = this.gui.app.dataSourceLoader.employeesDataSource.employeeIdToEmployee[id];
      if (employee) {
        if (!this.nodes.some((n) => (n.element as EmployeeDataType).Id === employee.Id)) {
          // only push node if there isn't aready a node with that employeeId. (to prevent duplicate nodes)
          this.nodes.push(new AccountNodeData(employee, relationshipType));
          this.links.push(new AccountLinkData(this.selectedElement.Id, employee.Id, relationshipType));
        } else {
          const existingLink = this.links.find((l) => l.target === employee.Id);
          existingLink.extraRelationships.push(relationshipType);
        }
      }
    }
  }

  protected forceXNode(node: AccountNodeData): number {
    if (node.relationshipType === AccountRelationshipType.HRProxy || node.relationshipType === AccountRelationshipType.AccountSPOC) {
      return this.svgCanvasWidth / 5.5;
    }
    if (
      node.relationshipType === AccountRelationshipType.AM ||
      node.relationshipType === AccountRelationshipType.ADM ||
      node.relationshipType === AccountRelationshipType.ACL
    ) {
      return this.svgCanvasWidth - this.svgCanvasWidth / 5.5;
    }
    return this.centerX;
  }

  protected forceYNode(node: AccountNodeData): number {
    if (node.relationshipType === AccountRelationshipType.ADM || node.relationshipType === AccountRelationshipType.AccountSPOC) {
      return this.svgCanvasHeight - this.svgCanvasHeight / 4;
    }
    if (node.relationshipType === AccountRelationshipType.HRProxy || node.relationshipType === AccountRelationshipType.ACL) {
      return this.svgCanvasHeight / 4;
    }
    return this.centerY;
  }

  protected linkClass(link: AccountLinkData): string {
    return 'account-relation-link';
  }

  protected linkLabelClass(link: AccountLinkData): string {
    return 'account-relation-link-label';
  }

  protected linkLabelText(link: AccountLinkData): string {
    return [link.relationshipType, ...link.extraRelationships].map((relationshipType) => {
      switch (relationshipType) {
        case AccountRelationshipType.ADM:
          return 'Delivery Owner ';
        case AccountRelationshipType.HRProxy:
          return 'HR Proxy ';
        case AccountRelationshipType.AccountSPOC:
          return 'Account SPOC ';
        case AccountRelationshipType.AM || AccountRelationshipType.ACL:
          return 'Commercial Owner ';
        default:
          return null;
      }
    }).filter(x => !!x).join(', ');
  }

  protected linkLabelX(link: AccountLinkData): number {
    const source = link.source as AccountNodeData;
    const target = link.target as AccountNodeData;
    return (
      source.x +
      (target.x - source.x) /
        (link.relationshipType === AccountRelationshipType.HRProxy || link.relationshipType === AccountRelationshipType.AccountSPOC ? 1.3 : 1.7)
    );
  }

  protected linkLabelY(link: AccountLinkData): number {
    const source = link.source as AccountNodeData;
    const target = link.target as AccountNodeData;
    let correction = -2;
    if ((source.y - 5 > target.y && source.x < target.x) || (source.y + 5 < target.y && source.x > target.x)) {
      correction = 12;
    }
    if (link.relationshipType === AccountRelationshipType.HRProxy) {
      correction = 22;
    }
    if (link.relationshipType === AccountRelationshipType.ADM) {
      correction = -10;
    }
    return (
      source.y +
      (target.y - source.y) /
        (link.relationshipType === AccountRelationshipType.HRProxy || link.relationshipType === AccountRelationshipType.ADM ? 1.3 : 1.5) +
      correction
    );
  }

  protected nodeClass(node: AccountNodeData): string {
    if (node.element.Id === this.selectedElement.Id) {
      return 'account-node';
    }
    return 'user-relation-node';
  }

  protected nodeCX(node: AccountNodeData): number {
    return node.x;
  }

  protected nodeLabelX(node: AccountNodeData, index: number, labels: d3.BaseType[] | d3.ArrayLike<d3.BaseType>): number {
    if (
      node.relationshipType === AccountRelationshipType.AM ||
      node.relationshipType === AccountRelationshipType.ADM ||
      node.relationshipType === AccountRelationshipType.ACL
    ) {
      return node.x + (this.relationCircleR || 0) * 1.2;
    }
    if (node.relationshipType === AccountRelationshipType.HRProxy || node.relationshipType === AccountRelationshipType.AccountSPOC) {
      return node.x - (labels[index] as HTMLElement).getBoundingClientRect().width - (this.relationCircleR || 0) * 1.2;
    }
    return node.x;
  }

  protected handleNodeClick(node: AccountNodeData) {
    if (node.element.Id !== this.selectedElement.Id) {
      this.gui.commandManager.selectUserCommandExecutor.requestExecute({
        userId: node.element._internal_datasource_id_,
      });
    }
  }

  protected getCenterNodeDetails() {
    return {
      topText: this.selectedElement.Name,
      bottomText: this.selectedElement.AccountClusterName
    };
  }
}
